/** @jsxImportSource @emotion/react */
import { useMemo, useState } from 'react'
import { Button, Typography, Accordion, AccordionDetails, AccordionSummary, IconButton } from '@mui/material'
import { Input } from 'components/Input/Input'
import { ReactComponent as ChevronDownIcon } from 'assets/icons/chevron-down.svg'
import { ReactComponent as DnDIcon } from 'assets/icons/dnd-upd.svg'
import Editor from 'components/Editor'
import { TableConstructor, TableProps } from 'components/TableConstructor'
import * as classes from './styles'

const MAX_COLUMNS_COUNT = 6

export type Section = {
  id: string
  title?: string
  description?: string
  table?: string
}

export type ParsedTable = {
  cols: TableProps['initialColumns']
  rows: TableProps['initialRows']
  isEmpty?: boolean
}

export type SectionFieldProps = {
  name: string
  index: number
  value: Section
  onChange: (value: Section) => void
  removeDisabled?: boolean
  onRemove: (value: Section, index: number) => void
  className?: string
}

export function SectionField({ name, index, value, onChange, removeDisabled, onRemove, className }: SectionFieldProps) {
  const [isExpanded, setIsExpanded] = useState(true)
  const isWithTable = !!value.table
  const parsedTable = useMemo(() => {
    if (!value.table) {
      return null
    }

    return JSON.parse(value.table) as ParsedTable
  }, [value.table])

  return (
    <Accordion key={name} expanded={isExpanded} css={classes.accordion} className={className}>
      <AccordionSummary css={classes.accordionSummary}>
        <Typography variant="title3" css={[classes.title /*, error && classes.error*/]}>
          {isWithTable ? 'Table' : 'Details'} Section #{index + 1}
        </Typography>
        <div css={classes.buttons}>
          <IconButton onClick={() => setIsExpanded((prev) => !prev)}>
            <ChevronDownIcon
              css={[classes.iconChevron, isExpanded && classes.iconChevronOpen]}
              data-testid="chevron-down-section-button"
            />
          </IconButton>
          <DnDIcon />
        </div>
      </AccordionSummary>
      <AccordionDetails css={classes.accordionDetails}>
        <Input
          label="Title"
          value={value.title ?? ''}
          onChange={(title) => onChange({ ...value, title })}
          placeholder="Enter Title"
          data-testid={isWithTable ? 'table-title-field' : 'details-title-field'}
          // error={!!error?.title}
          fullWidth
        />
        {isWithTable && parsedTable ? (
          <>
            <Input
              label="Description"
              value={value.description ?? ''}
              onChange={(description) => onChange({ ...value, description })}
              placeholder="Enter Description"
              data-testid="table-description-field"
              // error={!!error?.description}
              fullWidth
            />
            <TableConstructor
              id={value.id}
              initialColumns={parsedTable.cols}
              initialRows={parsedTable.rows}
              onChange={(cols, rows) => onChange({ ...value, table: JSON.stringify({ cols, rows }) })}
              initialIsColumnsEditable={parsedTable.isEmpty}
              maxColumnsCount={MAX_COLUMNS_COUNT}
              isSortable
            />
          </>
        ) : (
          <Editor
            label="Description"
            value={value.description ?? ''}
            onChange={(description) => onChange({ ...value, description })}
            placeholder="Enter Description"
            data-testid="details-description-field"
            // error={!!error?.description}
          />
        )}
        <Button
          variant="outlined"
          color="red"
          size="large"
          onClick={() => onRemove(value, index)}
          disabled={removeDisabled}
          fullWidth
          data-testid={isWithTable ? 'remove-table-section-button' : 'remove-details-section-button'}
        >
          Delete
        </Button>
      </AccordionDetails>
    </Accordion>
  )
}
